// Davet/sıfırlama bağlantısı ön kontrolü — FirstLogin + ResetPassword sayfalarının TEK kopya ve
// karar kaynağı (Finar, 2026-10-20: kullanılmış davet bağlantısı ikinci açılışta yine şifre
// formunu gösteriyordu; hata ancak "Hesabı Aktive Et"e basınca geliyordu).
//
// Sayfa açılınca POST /auth/activation-status {token} çağrılır:
//   valid   → form
//   used    → "daha önce kullanılmış" + giriş ekranı + şifremi unuttum
//   expired → "süresi dolmuş" + yeni bağlantı (reset) / yöneticinizle iletişime geçin (davet)
//   invalid → mevcut nötr metin + giriş bağlantısı
//   uç hata (ağ/5xx/429) → FORM (fail-open — eski davranış, kullanıcı engellenmesin)
// Müşteri kitlesi de bu sayfaları kullanır: metinlerde "AI"/"üretim"/"danışman notu" geçmez.

import type { ActivationStatusResponse } from "@mahir/shared";

export type ActivationLinkKind = "first_login" | "reset";

/** Sayfanın karar sonucu: form göster ya da engelle (engel türü + hangi uçtan geldiği). */
export type ActivationGate =
  | { mode: "form" }
  | { mode: "blocked"; status: "used" | "expired" | "invalid" };

export interface ActivationLinkNoticeCopy {
  title: string;
  body: string;
  /** Birincil düğme (varsa). */
  primary?: { label: string; to: string };
  /** İkincil bağlantı (varsa). */
  secondary?: { label: string; to: string };
}

export const ACTIVATION_LINK_COPY = {
  checking: "Bağlantı doğrulanıyor…",
  used: {
    title: "Bu bağlantı daha önce kullanılmış",
    body: "Şifrenizi belirlediyseniz giriş yapabilirsiniz.",
    primary: { label: "Giriş ekranına git", to: "/login" },
    secondary: { label: "Şifremi unuttum", to: "/forgot" },
  },
  expired: {
    first_login: {
      title: "Bağlantının süresi dolmuş",
      body: "Davet bağlantıları sınırlı süre geçerlidir. Yeni bir bağlantı için yöneticinizle veya danışmanınızla iletişime geçin.",
      secondary: { label: "Giriş ekranına git", to: "/login" },
    },
    reset: {
      title: "Bağlantının süresi dolmuş",
      body: "Şifre sıfırlama bağlantıları sınırlı süre geçerlidir. Yeni bir bağlantı isteyebilirsiniz.",
      primary: { label: "Yeni bağlantı iste", to: "/forgot" },
      secondary: { label: "Giriş ekranına git", to: "/login" },
    },
  },
  invalid: {
    first_login: {
      title: "Bağlantı geçersiz",
      body: "Aktivasyon linki geçersiz veya süresi dolmuş. Yöneticinizle veya danışmanınızla iletişime geçin.",
      secondary: { label: "Giriş ekranına git", to: "/login" },
    },
    reset: {
      title: "Bağlantı geçersiz",
      body: "Sıfırlama linki geçersiz veya süresi dolmuş. Lütfen tekrar şifre sıfırlama talep edin.",
      primary: { label: "Yeni bağlantı iste", to: "/forgot" },
      secondary: { label: "Giriş ekranına git", to: "/login" },
    },
  },
} as const satisfies {
  checking: string;
  used: ActivationLinkNoticeCopy;
  expired: Record<ActivationLinkKind, ActivationLinkNoticeCopy>;
  invalid: Record<ActivationLinkKind, ActivationLinkNoticeCopy>;
};

/** Uç yanıtından sayfa kararı. `valid` (ve tanınmayan değer) → form. */
export function resolveActivationGate(res: ActivationStatusResponse): ActivationGate {
  switch (res.status) {
    case "used":
    case "expired":
    case "invalid":
      return { mode: "blocked", status: res.status };
    case "valid":
    default:
      return { mode: "form" };
  }
}

/** Uç hata verdiğinde (ağ, 5xx, 429, bozuk gövde) karar: FORM — kullanıcı engellenmez. */
export function activationGateFromError(_err: unknown): ActivationGate {
  return { mode: "form" };
}

/** Engel türü + sayfa türü → gösterilecek metin seti. */
export function activationNoticeCopy(
  status: "used" | "expired" | "invalid",
  kind: ActivationLinkKind,
): ActivationLinkNoticeCopy {
  if (status === "used") return ACTIVATION_LINK_COPY.used;
  return ACTIVATION_LINK_COPY[status][kind];
}
